// Shared token-backed action primitive.
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import type { CSSProperties } from 'react';
import { lucideIcon } from './lucide-icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive-ghost' | 'favorite-ghost' | 'emphasis' | 'destructive' | 'danger' | 'amber';
/** `xs` is dense chrome — a popover header, a toolbar strip, a content row.
 * Input does not take it: 22px is below a comfortable target for typing. */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Lucide icon name (kebab-case) rendered before children */
  icon?: string;
  /** Renders `icon` after the label instead of before it. */
  iconRight?: boolean;
  /**
   * `between` pins the glyph to the far edge and lets the label take the team,
   * which is the trigger shape — a select-like button with a chevron on the
   * right. Call sites used to reach for justifyContent or a justify-between
   * class, and the latter is inert against this primitive's inline flex.
   */
  justify?: 'center' | 'between';
  /** Icon-only square button — pass icon, omit children */
  iconOnly?: boolean;
  /**
   * Render as child element via Radix Slot (for Radix compound components, or to make a
   * button-shaped link a real `<a>`). The child owns the content: `icon`/`iconRight`/
   * `children` composition is skipped, because Slot takes exactly one child and injecting
   * an icon beside it would break it. Put the icon inside your element instead.
   */
  asChild?: boolean;
  /**
   * On/off state for a button that toggles rather than acts — a view mode, a follow
   * state. Renders the shared "this one is on" treatment and sets `aria-pressed`.
   *
   * Not for selection within a set (that's Segmented/Tabs) and not for "a filter is
   * applied" (that's ToolbarIconButton's dot, which marks a control whose *effect* is
   * active elsewhere on the page).
   */
  pressed?: boolean;
  style?: CSSProperties;
}

// Size tiers read the shared control-size regime (tokens.css §10.1) so a Button
// aligns in a row with any other control at the same `size`. Height is fixed;
// padding is horizontal-only. iconOnly buttons are square (width == height).
const SIZES: Record<ButtonSize, { height: string; px: string; fontSize: string; radius: string; gap: string; icon: string }> = {
  xs: { height: 'var(--ds-control-h-xs)', px: 'var(--ds-control-px-xs)', fontSize: 'var(--ds-control-font-xs)', radius: 'var(--ds-control-radius-xs)', gap: 'var(--ds-control-gap-xs)', icon: 'var(--ds-control-icon-xs)' },
  sm: { height: 'var(--ds-control-h-sm)', px: 'var(--ds-control-px-sm)', fontSize: 'var(--ds-control-font-sm)', radius: 'var(--ds-control-radius-sm)', gap: 'var(--ds-control-gap-sm)', icon: 'var(--ds-control-icon-sm)' },
  md: { height: 'var(--ds-control-h-md)', px: 'var(--ds-control-px-md)', fontSize: 'var(--ds-control-font-md)', radius: 'var(--ds-control-radius-md)', gap: 'var(--ds-control-gap-md)', icon: 'var(--ds-control-icon-md)' },
  lg: { height: 'var(--ds-control-h-lg)', px: 'var(--ds-control-px-lg)', fontSize: 'var(--ds-control-font-lg)', radius: 'var(--ds-control-radius-lg)', gap: 'var(--ds-control-gap-lg)', icon: 'var(--ds-control-icon-lg)' },
};

export const BUTTON_VARIANTS: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: 'var(--ds-brand-gradient)',
    color: '#fff',
    border: '1px solid transparent',
    boxShadow: '0 12px 26px -13px hsl(285 75% 50% / 0.7)',
    fontWeight: 600,
  },
  secondary: {
    background: 'var(--ds-input-fill)',
    color: 'hsl(var(--ds-text-strong))',
    border: '1px solid var(--ds-control-border)',
    fontWeight: 600,
  },
  ghost: {
    background: 'transparent',
    color: 'hsl(var(--ds-text-meta))',
    border: '1px solid transparent',
    fontWeight: 600,
  },
  // Ghost that toggles a user-set favorite. Off is a plain ghost; `pressed` supplies
  // the gold wash and glyph, so the two states differ only in accent, not in weight.
  'favorite-ghost': {
    background: 'transparent',
    color: 'hsl(var(--ds-text-meta))',
    border: '1px solid transparent',
    fontWeight: 600,
  },
  // "Share the win" — celebration action; green health fill + soft glow ring
  emphasis: {
    background: 'var(--ds-health-green-gradient)',
    color: '#fff',
    border: '1px solid transparent',
    boxShadow: '0 0 0 4px hsl(160 70% 44% / 0.15), 0 14px 30px -12px hsl(160 70% 38% / 0.6)',
    fontWeight: 600,
  },
  // The lowest-commitment destructive: no fill at all, red glyph, red wash on
  // hover. For a row's trash icon, where `destructive`'s tint would put a filled
  // chip in every row and make deletion the loudest thing on screen.
  //
  // The three destructive weights read as one scale, and the choice is about how
  // much the action should announce itself before you reach for it:
  //   ghost   → an icon among icons  (row actions)
  //   tinted  → a labelled button    (inline "Delete Rock")
  //   solid   → the confirm itself   (dialog primary)
  'destructive-ghost': {
    background: 'transparent',
    color: 'hsl(var(--ds-health-red-fg))',
    border: '1px solid transparent',
    fontWeight: 600,
  },
  // Tinted, low-commitment destructive (e.g. inline "Delete Rock")
  destructive: {
    background: 'hsl(var(--ds-health-red-bg))',
    color: 'hsl(var(--ds-health-red-fg))',
    border: '1px solid color-mix(in srgb, hsl(var(--ds-health-red-fg)) 32%, transparent)',
    fontWeight: 600,
  },
  // Solid, high-commitment confirm (e.g. delete dialog confirm)
  danger: {
    background: 'var(--ds-health-red-gradient)',
    color: '#fff',
    border: '1px solid transparent',
    boxShadow: '0 12px 26px -13px hsl(2 75% 50% / 0.7)',
    fontWeight: 600,
  },
  // Tinted, low-commitment amber — a caution-toned action alternative to primary/secondary,
  // same weight class as `destructive` (health-tinted, not solid) for a toggle/inline action.
  amber: {
    background: 'hsl(var(--ds-health-amber-bg))',
    color: 'hsl(var(--ds-health-amber-fg))',
    border: '1px solid color-mix(in srgb, hsl(var(--ds-health-amber-fg)) 32%, transparent)',
    fontWeight: 600,
  },
};

export const BUTTON_DISABLED_STYLE: CSSProperties = {
  background: 'hsl(var(--ds-neutral-bg))',
  color: 'hsl(var(--ds-text-meta))',
  border: '1px solid transparent',
  boxShadow: 'none',
  opacity: 0.9,
  cursor: 'not-allowed',
};

// Ghost has no fill to begin with — giving a disabled ghost button a filled background
// makes it read as MORE prominent than a clickable one, backwards from what disabled
// should communicate. Keep it transparent and just mute the glyph/label instead.
const BUTTON_DISABLED_GHOST_STYLE: CSSProperties = {
  background: 'transparent',
  color: 'hsl(var(--ds-text-meta))',
  border: '1px solid transparent',
  boxShadow: 'none',
  opacity: 0.45,
  cursor: 'not-allowed',
};

// Pressed is a low-opacity wash of the brand *foreground*, the same construction the
// destructive/amber variants use — not `--ds-brand-purple-bg`, which is a solid tint fill
// meant for large areas like YearGrid's current row and is documented as needing low
// opacity in Aurora anyway. Deriving from the fg token makes one expression correct in
// both themes: it rides whatever lightness each theme already tuned for legibility.
//
// Deliberately restrained: a pressed ghost sits in a row of unpressed ghosts and should
// read as lit, not as promoted to primary. Most of the signal is the purple glyph.
const pressedBg = (accentFg: string) => `hsl(var(${accentFg}) / 0.12)`;
// Hover on a pressed button deepens the same wash. It must not fall through to the neutral
// ghost hover, which would make a pressed button look unpressed exactly when you point at it.
const pressedHoverBg = (accentFg: string) => `hsl(var(${accentFg}) / 0.2)`;

const pressedStyle = (accentFg: string): CSSProperties => ({
  background: pressedBg(accentFg),
  color: `hsl(var(${accentFg}))`,
});

// The fg token a variant's pressed wash and glyph derive from. Brand purple is the default
// because a pressed ghost normally means "this one is on" in the product's own voice; a
// variant that already owns an accent names it here so its on-state stays in that accent
// rather than turning purple under the thumb.
const VARIANT_ACCENT_FG: Partial<Record<ButtonVariant, string>> = {
  'destructive-ghost': '--ds-health-red-fg',
  'favorite-ghost': '--ds-favorite-fg',
};

// Pressed *and* disabled — an on-state you can see but not change: the Rock owner
// implicitly follows their own rock and can't unfollow. It must not fall through to the
// plain disabled style, which would report the state as off. Keeps the pressed treatment
// and mutes it, rather than inventing a third appearance.
const pressedDisabledStyle = (accentFg: string): CSSProperties => ({
  ...pressedStyle(accentFg),
  border: '1px solid transparent',
  boxShadow: 'none',
  opacity: 0.55,
  cursor: 'not-allowed',
});

// Ghost is a WEIGHT, not one variant: both fill-less variants need the same
// disabled treatment and the same imperative hover, because there is no fill for
// `brightness()` to act on. Keyed rather than compared by name so a third ghost
// never has to find all three branches again.
const GHOST_WEIGHT: ReadonlySet<ButtonVariant> = new Set(['ghost', 'destructive-ghost', 'favorite-ghost']);

const GHOST_HOVER_BG: Partial<Record<ButtonVariant, string>> = {
  ghost: 'var(--ds-ghost-hover-bg)',
  // Derived from the same fg token the glyph uses, so one expression is correct
  // in both themes — the construction pressedBg uses, for the same reason.
  'destructive-ghost': 'hsl(var(--ds-health-red-fg) / 0.12)',
  'favorite-ghost': 'hsl(var(--ds-favorite-fg) / 0.12)',
};

function Icon({ name, size }: { name: string; size: string }) {
  const IconComp = lucideIcon(name);
  return IconComp ? <IconComp width={size} height={size} strokeWidth={2} aria-hidden /> : null;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      icon,
      iconRight = false,
      justify = 'center',
      iconOnly = false,
      disabled = false,
      asChild = false,
      pressed,
      style,
      className,
      onMouseEnter,
      onMouseLeave,
      ...rest
    },
    ref,
  ) => {
    const sz = SIZES[size];
    const v = BUTTON_VARIANTS[variant];

    // iconOnly declares a shape, not a content switch: with no `icon` the
    // children are the glyph, which is what stops a self-drawn mark rendering
    // as an empty square.
    const leading = icon ?? (iconOnly ? children : undefined);
    const glyph = leading == null ? null : (
      typeof leading === 'string'
        ? <Icon name={leading} size={iconOnly ? sz.icon : '1em'} />
        // A node keeps the size it brings; the slot owns placement and the gap,
        // which is what a call site could not do for itself.
        : <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{leading}</span>
    );

    const accentFg = VARIANT_ACCENT_FG[variant] ?? '--ds-brand-purple-fg';

    const baseStyle: CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: justify === 'between' ? 'space-between' : 'center',
      gap: sz.gap,
      height: sz.height,
      fontFamily: 'var(--ds-font-body)',
      fontSize: sz.fontSize,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      borderRadius: sz.radius,
      cursor: 'pointer',
      boxSizing: 'border-box',
      transition: 'transform var(--ds-dur-fast) var(--ds-ease-soft), box-shadow var(--ds-dur-fast) var(--ds-ease-soft), filter var(--ds-dur-fast) var(--ds-ease-soft)',
      outline: 'none',
      // square: width == height, glyph sized to the tier's icon token
      ...(iconOnly
        ? { padding: 0, width: sz.height, gap: 0, fontSize: sz.icon }
        : { padding: '0 ' + sz.px }),
      ...(disabled
        ? (pressed
            ? pressedDisabledStyle(accentFg)
            : (GHOST_WEIGHT.has(variant) ? BUTTON_DISABLED_GHOST_STYLE : BUTTON_DISABLED_STYLE))
        : v),
      ...(pressed && !disabled ? pressedStyle(accentFg) : null),
      ...style,
    };

    // The ghost hover handlers write `background` imperatively, so they need to know what
    // rest and hover actually are — for a pressed ghost, both are the accent wash. Rest
    // stays pressed-aware even when disabled, so a stray mouseleave can't wipe the wash.
    const ghostRestBg = (pressed ? pressedBg(accentFg) : v.background) as string;
    const ghostHoverBg = disabled ? ghostRestBg
      : (pressed ? pressedHoverBg(accentFg) : (GHOST_HOVER_BG[variant] ?? 'var(--ds-ghost-hover-bg)'));

    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        disabled={disabled}
        aria-pressed={pressed}
        className={className ? `ds-focusable ${className}` : 'ds-focusable'}
        style={baseStyle}
        onMouseEnter={(e) => {
          if (!disabled) {
            const el = e.currentTarget as HTMLElement;
            // Ghost has no fill for brightness() to act on — tint the background instead.
            if (GHOST_WEIGHT.has(variant)) el.style.background = ghostHoverBg;
            else el.style.filter = 'brightness(var(--ds-hover-brightness))';
          }
          onMouseEnter?.(e as React.MouseEvent<HTMLButtonElement>);
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLElement;
          if (GHOST_WEIGHT.has(variant)) el.style.background = ghostRestBg;
          else el.style.filter = '';
          onMouseLeave?.(e as React.MouseEvent<HTMLButtonElement>);
        }}
        {...rest}
      >
        {asChild ? children : (
          <>
            {!iconRight && glyph}
            {!iconOnly && (justify === 'between' ? <span style={{ flex: 1, minWidth: 0, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span> : children)}
            {iconRight && glyph}
          </>
        )}
      </Comp>
    );
  },
);

Button.displayName = 'Button';
